/**
 * Modifier une intervention déjà créée (U6, ce qui manquait après le retrait de l'ancien planning).
 *
 * Le serveur sait tout corriger par UN seul chemin (`PATCH /planning/interventions/:id`) : nature, demande du client, n° de
 * commande, « à confirmer », échéance, jours, équipe, durée, machine d'une intervention « à identifier ». Il manquait l'écran.
 * Ici, la logique pure : on part du briefing (ce que le serveur dit), on laisse l'utilisateur corriger un formulaire, et on ne
 * RENVOIE QUE ce qui a changé — une clé absente ne change rien côté serveur, et un envoi superflu ferait écraser par erreur ce
 * que quelqu'un d'autre vient de corriger.
 *
 * Trois règles du serveur que l'écran respecte :
 *  - les jours et l'équipe se donnent en LISTES COMPLÈTES : l'équipe cochée s'applique à TOUS les jours ; retirer un jour écarte
 *    son équipe (même les heures déjà saisies : l'écran le dit avant) ;
 *  - la durée prévue est celle de CHAQUE technicien, chaque jour : sans technicien elle ne s'applique à personne ;
 *  - une machine ne se REMPLACE pas : seule une intervention « à identifier » en reçoit une.
 */
import type { BriefingIntervention, ModificationIntervention } from '../api/client';
import { ajouterJours, dureePourSaisie, lireDuree, DUREE_MAX_MINUTES, DUREE_MIN_MINUTES } from './planning';

/** Le plafond du serveur (`JOURS_PLAGE_MAX`) : une plage plus longue serait refusée. */
export const JOURS_MAX = 366;

export interface FormulaireModification {
  readonly natureId: string;
  /** Atelier seulement. */
  readonly titre: string;
  readonly description: string;
  readonly commande: string;
  readonly tentative: boolean;
  /** Les jours travaillés, AAAA-MM-JJ, triés. */
  readonly jours: readonly string[];
  /** L'échéance : les deux dates ou aucune. */
  readonly fenetreDebut: string;
  readonly fenetreFin: string;
  /** Les identifiants des techniciens cochés. */
  readonly techniciens: readonly string[];
  /** Saisie libre (« 8 », « 7,5 », « 1h30 ») : la durée prévue de chaque technicien, chaque jour. */
  readonly duree: string;
  /** Le choix d'une machine pour une intervention « à identifier » ; vide = pas de choix. */
  readonly machineId: string;
}

/** Ce que le briefing dit et que le formulaire ne montre pas, mais dont l'enregistrement dépend. */
export interface ContexteModification {
  readonly revision: number;
  readonly atelier: boolean;
  /** Chez un client, sans machine : le seul cas où l'on peut en choisir une. */
  readonly machineAIdentifier: boolean;
  /** Tous les jours ont la même équipe (ou il n'y a aucun jour) : cocher une équipe ne change alors rien d'autre que ce qu'on voit. */
  readonly equipesIdentiques: boolean;
  /** Les durées prévues diffèrent d'un technicien ou d'un jour à l'autre : la case reste vide, et vide veut dire « inchangé ». */
  readonly dureeMixte: boolean;
  /** Les jours qui portent déjà des heures SAISIES : les retirer les écarte. */
  readonly joursAvecHeures: readonly string[];
}

const FORME_JOUR = /^\d{4}-\d{2}-\d{2}$/;

/** Un jour AAAA-MM-JJ qui existe vraiment (pas le 31 février). */
export function jourValide(jour: string): boolean {
  if (!FORME_JOUR.test(jour)) return false;
  const d = new Date(`${jour}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === jour;
}

const trie = (jours: readonly string[]): string[] => [...new Set(jours)].sort();

/** Deux listes de la même chose, sans tenir compte de l'ordre. */
function memeEnsemble(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');
}

/** Le formulaire de départ et son contexte, lus du briefing. */
export function formulaireDepuis(b: BriefingIntervention): { readonly formulaire: FormulaireModification; readonly contexte: ContexteModification } {
  const i = b.intervention;
  const jours = trie(b.jours.map((j) => j.day));

  const equipes = b.jours.map((j) => j.members.map((m) => m.technicianId).sort().join('|'));
  const equipesIdentiques = equipes.every((e) => e === equipes[0]);
  const techniciens: string[] = [];
  for (const j of b.jours) for (const m of j.members) if (!techniciens.includes(m.technicianId)) techniciens.push(m.technicianId);

  const durees = b.jours.flatMap((j) => j.members.map((m) => m.minutesPlanned));
  const premiere = durees[0];
  const uniforme = durees.every((d) => d === premiere);
  const dureeMixte = durees.length > 0 && !uniforme;

  return {
    formulaire: {
      natureId: i.natureId ?? '',
      titre: i.title ?? '',
      description: i.description ?? '',
      commande: i.customerOrderRef ?? '',
      tentative: i.tentative,
      jours,
      fenetreDebut: i.windowStart ?? '',
      fenetreFin: i.windowEnd ?? '',
      techniciens,
      duree: uniforme && premiere !== undefined ? dureePourSaisie(premiere) : '',
      machineId: '',
    },
    contexte: {
      revision: i.revision,
      atelier: i.kind === 'WORKSHOP',
      machineAIdentifier: i.kind === 'CUSTOMER' && i.asset === null,
      equipesIdentiques,
      dureeMixte,
      joursAvecHeures: b.jours.filter((j) => j.members.some((m) => m.minutesEntered !== null)).map((j) => j.day),
    },
  };
}

/** Le formulaire a-t-il bougé depuis son ouverture ? (garde « travail non enregistré » à la fermeture.) */
export function formulaireModifie(depart: FormulaireModification, actuel: FormulaireModification): boolean {
  return (
    depart.natureId !== actuel.natureId ||
    depart.titre !== actuel.titre ||
    depart.description !== actuel.description ||
    depart.commande !== actuel.commande ||
    depart.tentative !== actuel.tentative ||
    depart.fenetreDebut !== actuel.fenetreDebut ||
    depart.fenetreFin !== actuel.fenetreFin ||
    depart.duree !== actuel.duree ||
    depart.machineId !== actuel.machineId ||
    !memeEnsemble(depart.jours, actuel.jours) ||
    !memeEnsemble(depart.techniciens, actuel.techniciens)
  );
}

// --- Les jours -------------------------------------------------------------------------------------------------

/** Ajoute un jour saisi ; `null` quand il est illisible (le champ date vide ou inachevé). */
export function ajouterUnJour(jours: readonly string[], jour: string): string[] | null {
  return jourValide(jour) ? trie([...jours, jour]) : null;
}

export function retirerUnJour(jours: readonly string[], jour: string): string[] {
  return jours.filter((j) => j !== jour);
}

/**
 * Les jours d'une plage (bornes comprises), les jours ouvrés seulement si demandé (samedi et dimanche écartés) ; ou le refus en
 * français. Une plage plus longue que le plafond du serveur est refusée ici plutôt que par lui.
 */
export function joursDeLaPlage(du: string, au: string, joursOuvres: boolean): { readonly jours: string[] } | { readonly refus: string } {
  if (!jourValide(du) || !jourValide(au)) return { refus: 'Choisis le premier et le dernier jour de la plage.' };
  if (au < du) return { refus: 'Le dernier jour de la plage est avant le premier.' };
  const jours: string[] = [];
  for (let j = du; j <= au; j = ajouterJours(j, 1)) {
    if (jours.length >= JOURS_MAX) return { refus: `Une plage va jusqu’à ${JOURS_MAX} jours.` };
    const dow = new Date(`${j}T00:00:00.000Z`).getUTCDay();
    if (!joursOuvres || (dow !== 0 && dow !== 6)) jours.push(j);
  }
  if (jours.length === 0) return { refus: 'Aucun jour ouvré dans cette plage.' };
  return { jours };
}

// --- L'équipe --------------------------------------------------------------------------------------------------

export function basculerTechnicien(techniciens: readonly string[], id: string): string[] {
  return techniciens.includes(id) ? techniciens.filter((t) => t !== id) : [...techniciens, id];
}

// --- Ce qu'on envoie -------------------------------------------------------------------------------------------

export type ResultatModification =
  /** Rien n'a changé : « Enregistrer » reste grisé. */
  | { readonly rien: true }
  /** Ce qui empêche d'enregistrer, en français. */
  | { readonly erreurs: readonly string[] }
  | {
      readonly corps: ModificationIntervention;
      /** Les jours retirés qui portaient des heures saisies : à confirmer avant d'écrire. */
      readonly joursRetiresAvecHeures: readonly string[];
      /** Les jours ajoutés qui n'auront pas d'équipe (les équipes différaient d'un jour à l'autre) : à dire après. */
      readonly joursSansEquipe: readonly string[];
    };

const AIDE_DUREE = `La durée prévue va de ${DUREE_MIN_MINUTES} minutes à ${DUREE_MAX_MINUTES / 60} heures (« 8 », « 7,5 », « 1h30 »).`;

/**
 * Compare le formulaire à son départ et construit ce qu'il faut envoyer — RIEN que ce qui a changé. Les refus sont ceux qu'on peut
 * dire avant d'appeler le serveur ; il garde les siens (révision périmée, machine d'un autre site, technicien archivé…).
 */
export function modificationDeLIntervention(depart: FormulaireModification, actuel: FormulaireModification, ctx: ContexteModification): ResultatModification {
  const erreurs: string[] = [];
  const corps: { -readonly [K in keyof ModificationIntervention]: ModificationIntervention[K] } = { revision: ctx.revision };

  if (ctx.atelier) {
    if (actuel.titre !== depart.titre) {
      const titre = actuel.titre.trim();
      if (titre === '') erreurs.push('Le titre d’une intervention d’atelier ne peut pas être vide.');
      else corps.title = titre;
    }
  } else if (actuel.natureId !== depart.natureId) {
    if (actuel.natureId === '') erreurs.push('Choisis la nature de l’intervention.');
    else corps.natureId = actuel.natureId;
  }

  const description = actuel.description.trim();
  if (description !== depart.description.trim()) corps.description = description === '' ? null : description;
  const commande = actuel.commande.trim();
  if (commande !== depart.commande.trim()) corps.customerOrderRef = commande === '' ? null : commande;
  if (actuel.tentative !== depart.tentative) corps.tentative = actuel.tentative;

  // L'échéance : les deux dates ou aucune (le serveur la modifie en entier).
  if (actuel.fenetreDebut !== depart.fenetreDebut || actuel.fenetreFin !== depart.fenetreFin) {
    const debut = actuel.fenetreDebut;
    const fin = actuel.fenetreFin;
    if (debut === '' && fin === '') {
      corps.windowStart = null;
      corps.windowEnd = null;
    } else if (debut === '' || fin === '') {
      erreurs.push('Une échéance a deux dates : « à partir du » et « au plus tard le » (ou aucune des deux).');
    } else if (!jourValide(debut) || !jourValide(fin)) {
      erreurs.push('Les dates de l’échéance sont illisibles.');
    } else if (fin < debut) {
      erreurs.push('Le « au plus tard le » est avant le « à partir du ».');
    } else {
      corps.windowStart = debut;
      corps.windowEnd = fin;
    }
  }

  const joursChanges = !memeEnsemble(depart.jours, actuel.jours);
  const joursAjoutes = actuel.jours.filter((j) => !depart.jours.includes(j));
  const joursRetires = depart.jours.filter((j) => !actuel.jours.includes(j));
  if (joursChanges) {
    if (actuel.jours.length > JOURS_MAX) erreurs.push(`Une intervention va jusqu’à ${JOURS_MAX} jours.`);
    else corps.jours = trie(actuel.jours);
  }

  // L'équipe cochée s'applique à TOUS les jours. Si on n'y touche pas mais qu'on ajoute des jours, les jours neufs reprennent
  // l'équipe — quand elle est la même partout ; sinon ils restent sans équipe (« à affecter ») et l'écran le dit.
  const equipeChangee = !memeEnsemble(depart.techniciens, actuel.techniciens);
  let joursSansEquipe: string[] = [];
  if (equipeChangee) {
    corps.technicianIds = [...actuel.techniciens];
  } else if (joursAjoutes.length > 0) {
    if (ctx.equipesIdentiques && depart.techniciens.length > 0) corps.technicianIds = [...depart.techniciens];
    else joursSansEquipe = joursAjoutes;
  }

  // La durée est celle de chaque technicien : vide = « inchangé » quand les durées étaient mélangées.
  if (actuel.duree.trim() !== depart.duree.trim()) {
    const minutes = lireDuree(actuel.duree);
    if (minutes === 'ERREUR') {
      erreurs.push(AIDE_DUREE);
    } else {
      const equipeFinale = corps.technicianIds ?? depart.techniciens;
      if (minutes !== null && (equipeFinale.length === 0 || actuel.jours.length === 0)) {
        erreurs.push('La durée prévue est celle de chaque technicien, chaque jour : choisis d’abord au moins un jour et un technicien.');
      } else {
        corps.dureeMinutes = minutes;
      }
    }
  } else if (joursAjoutes.length > 0 && corps.technicianIds !== undefined && corps.technicianIds.length > 0) {
    // Un jour ajouté avec son équipe reprend aussi la durée prévue, quand elle est la même partout (le champ est vide quand les
    // durées étaient mélangées : rien à reprendre). Même valeur que les jours existants : leur durée ne change pas.
    const minutes = lireDuree(actuel.duree);
    if (typeof minutes === 'number') corps.dureeMinutes = minutes;
  }

  if (ctx.machineAIdentifier && actuel.machineId !== '') corps.assetId = actuel.machineId;

  if (erreurs.length > 0) return { erreurs };
  if (Object.keys(corps).length === 1) return { rien: true };
  return {
    corps,
    joursRetiresAvecHeures: joursRetires.filter((j) => ctx.joursAvecHeures.includes(j)).sort(),
    joursSansEquipe,
  };
}

// --- Les phrases -----------------------------------------------------------------------------------------------

const NOM_CHAMP: Readonly<Record<string, string>> = {
  natureId: 'nature',
  title: 'titre',
  description: 'demande du client',
  customerOrderRef: 'n° de commande',
  tentative: '« à confirmer »',
  windowStart: 'échéance',
  assetId: 'machine',
  jours: 'jours',
  technicianIds: 'équipe',
  dureeMinutes: 'durée prévue',
};

/** « Intervention modifiée : nature, jours, équipe. » — les NOMS des champs changés, jamais leurs valeurs. */
export function phraseModification(corps: ModificationIntervention, joursSansEquipe: readonly string[]): string {
  const noms = Object.keys(corps)
    .filter((k) => k !== 'revision' && k !== 'windowEnd')
    .map((k) => NOM_CHAMP[k] ?? k);
  const base = noms.length === 0 ? 'Intervention modifiée.' : `Intervention modifiée : ${noms.join(', ')}.`;
  if (joursSansEquipe.length === 0) return base;
  const n = joursSansEquipe.length;
  return `${base} ${n === 1 ? '1 jour ajouté n’a' : `${n} jours ajoutés n’ont`} pas encore d’équipe : ${n === 1 ? 'il attend' : 'ils attendent'} sur la ligne « À affecter ».`;
}

/** La question avant de retirer des jours qui portent des heures saisies. */
export function questionJoursAvecHeures(jours: readonly string[]): string {
  const liste = jours.map((j) => j.split('-').reverse().join('/')).join(', ');
  return `${jours.length === 1 ? 'Le jour' : 'Les jours'} ${liste} ${jours.length === 1 ? 'porte' : 'portent'} des heures déjà saisies : ${
    jours.length === 1 ? 'le retirer écarte' : 'les retirer écarte'
  } l’équipe et ses heures de cette intervention. Continuer ?`;
}
